import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'components/Luxkit/Typography/Heading'
import { capitalise } from 'lib/string/stringUtils'
import { HOME_PAGE_OFFER_TYPES } from 'home/constants'
import Pane from 'components/Common/Pane'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import GeoContext from 'contexts/geoContext'
import { getHomeOfferListParams } from 'home/helpers'
import { usePlaceByName } from 'hooks/usePlace'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { themeClassName } from 'lib/theme/themeUtils'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import * as Analytics from 'analytics/analytics'
import { clickCarouselOffer } from 'analytics/optimizely/events'

const Wrapper = styled(Pane)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

 ${mediaQueryUp.desktop} {
   padding-top: ${rem(80)};
 }
`

interface Props {
  destination?: string;
  maxCarouselItemCount?: number;
}

function HeroOfferCarouselSection(props: Props) {
  const { currentRegionCode, geoStateName } = useContext(GeoContext)
  const destinationId = usePlaceByName(props.destination)?.id ?? ANYWHERE_PLACE_ID

  const showMoreOfferOnHeroCarousel = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.showMoreOffersOnHeroCarousel)

  const filters = useMemo(() => {
    if (props.destination) {
      return {
        offerTypes: HOME_PAGE_OFFER_TYPES,
        limit: props.maxCarouselItemCount,
        placeIds: [destinationId],
      }
    } else {
      return getHomeOfferListParams({
        region: currentRegionCode,
        subregion: geoStateName,
      })
    }
  }, [currentRegionCode, destinationId, geoStateName, props.destination, props.maxCarouselItemCount])

  const additionalOnOfferClick = () => {
    Analytics.track('click-carousel-offer', clickCarouselOffer())
  }

  return (
    <Wrapper paletteType="inverse">
      <Group direction="vertical" gap={24}>
        <LayoutContainer>
          {!props.destination && <Heading variant="heading2">
            Today's top <i>exclusive</i> offers
          </Heading>}
          {props.destination && (
            <Heading variant="heading2">
              Today's top exclusive <i>{capitalise(props.destination)}</i> offers
            </Heading>
          )}
        </LayoutContainer>
        <OfferListCarousel
          tileStyle={showMoreOfferOnHeroCarousel ? 'card' : 'hero'}
          filters={filters}
          className={themeClassName({ desktopAndUp: showMoreOfferOnHeroCarousel ? 'inverse' : 'default' })}
          additionalOnOfferClick={additionalOnOfferClick}
        />
      </Group>
    </Wrapper>
  )
}

export default React.memo(HeroOfferCarouselSection)
